import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTheme } from "@mui/material";
import { showError } from "../../storage/storageUtils";
import { MuiDataGrid, MuiDataGridProps } from "../muiDataGrid";
import {
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    GridCellParams,
    gridExpandedSortedRowEntriesSelector,
    GridInitialState,
    GridPaginationModel,
    GridRowEditStopParams,
    GridRowEditStopReasons,
    GridRowId,
    GridRowModes,
    GridRowModesModel,
    GridRowParams,
    GridSortModel,
    MuiEvent,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { usePrompt } from "../../../hooks/usePrompt";
import { useHistory } from "react-router-dom";
import { CustomToolbar } from "./customToolbar";
import useEmployeeUiState from "../../../hooks/useEmployeeUiState";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { CustomPagination } from "./customPagination";
import { useDebounce } from "../../hooks/useDebounce";
import ReactDOM from "react-dom";

interface GridRow {
    id: string;
}

export interface FieldError {
    field: string;
    error: string;
}

interface IMuiEditableDataGridProps<T> {
    rows: (T & Partial<GridRow>)[];
    setRows: React.Dispatch<React.SetStateAction<(T & Partial<GridRow>)[]>>;
    editId?: string;
    setEditId?: (id: string) => void;
    addRowText?: string;
    handleAddRow: () => T & Partial<GridRow>;
    handleSubmit: (row: T & Partial<GridRow>) => Promise<T & Partial<GridRow>>;
    validateRow?: (row: T) => FieldError[]; // Return all errors;
    detailsComponent?: (
        props: GridRowParams<T> & {
            updateFields: (
                id: string,
                values: { field: string; value: any; errors?: any }[]
            ) => void;
            hiddenColumns: string[];
        }
    ) => JSX.Element;
    persistStateKey?: string;
    highlightedRowId?: string;
    setHighlightedRowId?: (id: string) => void;
    fieldToFocus?: string;
    defaultSortModel?: GridSortModel;
    onPaginationModelChange?: (model: GridPaginationModel) => void;
    disableAddRowButton?: boolean;
    disableColumnSelector?: boolean;
    autosizeColumnsByName?: string[];
}

interface MuiEditableDataGridState {
    hiddenColumns: string[];
}

type MuiEditableDataGridProps<T> = IMuiEditableDataGridProps<T> &
    MuiDataGridProps;

export function MuiEditableDataGrid<T>({
    editId: propEditId,
    setEditId: propSetEditId,
    addRowText,
    handleAddRow,
    handleSubmit,
    validateRow,
    rows,
    setRows,
    detailsComponent: DetailsComponent,
    persistStateKey,
    highlightedRowId,
    setHighlightedRowId,
    fieldToFocus,
    defaultSortModel,
    paginationMode = "client",
    rowCount,
    paginationModel: propPaginationModel,
    onPaginationModelChange,
    sortingMode = "client",
    disableAddRowButton = false,
    disableColumnSelector = false,
    autosizeColumnsByName = [],
    ...props
}: MuiEditableDataGridProps<T>) {
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        props.pagination ? { pageSize: 25, page: 0 } : undefined
    );
    const [sortModel, setSortModel] = useState<GridSortModel>(
        defaultSortModel ?? []
    );
    const [editId, setEditId] = useState(propEditId);
    const theme = useTheme();
    const { t } = useTranslation();
    const originalRow = useRef<{ [id: string]: T }>({});
    const prompt = useRef<{ path: string; shouldGo?: boolean }>();
    const history = useHistory();
    const apiRef = useGridApiRef();

    useEffect(() => {
        if (typeof propEditId !== "undefined" && propEditId !== editId) {
            setEditId(propEditId);
        }
    }, [propEditId]);

    useEffect(() => {
        if (!!propSetEditId && propEditId !== editId) {
            propSetEditId(editId);
        }
    }, [editId]);

    const {
        value: persistedGridState,
        setValue: setPersistedGridState,
        initialized: gridStateInitialized,
    } = useEmployeeUiState<GridInitialState & MuiEditableDataGridState>(
        persistStateKey,
        { hiddenColumns: [] }
    );

    useEffect(() => {
        // Forces auto sizing for datagrid columns after all data is loaded
        if (apiRef.current?.autosizeColumns !== undefined &&
            autosizeColumnsByName?.length > 0
        ) {
            autosizeTableColumns();
        }
    }, [apiRef, rows, autosizeColumnsByName]);

    const autosizeTableColumns = useDebounce(() => {
        ReactDOM.flushSync(() => {
            void apiRef.current?.autosizeColumns({
                columns: autosizeColumnsByName,
                includeOutliers: true,
                includeHeaders: true,
            });
        });
    }, 500);

    useEffect(() => {
        if (gridStateInitialized) {
            setHiddenColumns(persistedGridState?.current.hiddenColumns ?? []);

            if (
                !!persistedGridState?.current?.pagination?.paginationModel
                    ?.pageSize
            ) {
                setPaginationModel((model) => ({
                    ...model,
                    pageSize:
                        persistedGridState.current.pagination.paginationModel
                            .pageSize,
                }));
            }
        }
    }, [gridStateInitialized]);

    const persistGridState = useCallback((hiddenColumns: string[], pageSize?: number) => {
        if (apiRef?.current?.exportState) {
            const currentState = apiRef.current.exportState();
            setPersistedGridState({
                columns: {
                    orderedFields: currentState.columns.orderedFields,
                },
                hiddenColumns,
                pagination: {
                    paginationModel: {
                        pageSize:
                            pageSize ??
                            currentState.pagination.paginationModel.pageSize,
                    },
                },
            });
        }
    }, [apiRef]);

    useEffect(() => {
        if (
            !!hiddenColumns &&
            // Check if hiddenColumns has actually changed before saving new state.
            hiddenColumns.sort().join("") !==
                (persistedGridState?.current.hiddenColumns ?? []).join("")
        ) {
            persistGridState(hiddenColumns);
        }
    }, [hiddenColumns]);

    const editModeId = useMemo(
        () =>
            Object.keys(rowModesModel).find(
                (k) => rowModesModel[k].mode === GridRowModes.Edit
            ),
        [rowModesModel]
    );

    // This is only for entering edit mode
    // Don't exit edit mode until changes haven been saved/rejected.
    useEffect(() => {
        if (!!editModeId) {
            setEditId(editModeId);

            const row = rows.find((r) => r.id === editModeId);
            if (!!row) {
                originalRow.current[editModeId] = { ...row };
            }
        }
    }, [editModeId]);

    // Prompt always resolves to false to wait for processing the rowUpdateCallback
    // Use this to navigate if changes were successfully saved right after prompt.
    useEffect(() => {
        if (!editId && !!prompt.current?.path && prompt.current?.shouldGo) {
            history.push(prompt.current?.path);
        }
    }, [editId]);

    usePrompt(!!editId, (path) => {
        stopEditing(path);
        // Saving is done after state change so we will have to wait for that to complete
        // Resolve as false to block the navigation for now.
        return new Promise<boolean>((resolve) => resolve(false));
    });

    const revertEditMode = useCallback(
        (prevModel: GridRowModesModel, ignoreModifications: boolean) =>
            Object.keys(prevModel).reduce(
                (acc, id) => ({
                    ...acc,
                    [id]: { mode: GridRowModes.View, ignoreModifications },
                }),
                {}
            ),
        []
    );

    const startRowEdit = useCallback(
        (id: GridRowId, fieldToFocus?: string) =>
            setRowModesModel((prevModel) => {
                return {
                    // Revert the mode of the other cells from other rows
                    ...revertEditMode(prevModel, true),
                    [id]: { mode: GridRowModes.Edit, fieldToFocus },
                };
            }),
        [revertEditMode, fieldToFocus]
    );

    const revertRows = (editId: string) => {
        setRows((oldRows) => {
            const newRows = [...oldRows.filter((r) => r.id !== "new")];
            const idx = newRows.findIndex((r) => r.id === editId);

            if (idx !== -1) {
                if (!!originalRow.current[editId]) {
                    newRows[idx] = originalRow.current[editId];
                    delete originalRow.current[editId];
                }
            }

            return newRows;
        });
    };

    const stopEditing = useCallback(
        (path?: string) => {
            prompt.current = { path, shouldGo: false }; // Path gets nulled if save is triggered manually.
            setRowModesModel((prevModel) => revertEditMode(prevModel, false));
        },
        [revertEditMode]
    );

    const cancelEdit = useCallback((editId: string) => {
        setRowModesModel((prevModel) => revertEditMode(prevModel, true));
        revertRows(editId);
        setEditId(null);
    }, [revertEditMode]);

    const handleCellClick = useCallback(
        (params: GridCellParams, event: React.MouseEvent) => {
            if (!params.isEditable || !!editId) {
                return;
            }

            // Ignore portal
            if (
                (event.target as any).nodeType === 1 &&
                !event.currentTarget.contains(event.target as Element)
            ) {
                return;
            }

            startRowEdit(params.id, params.field);
        },
        [startRowEdit, editId]
    );

    const handleRowModesModelChange = useCallback(
        (newModel: GridRowModesModel) => {
            setRowModesModel(newModel);
        },
        []
    );

    const handleAdd = () => {
        const newRow = handleAddRow();
        const id = "new";
        newRow.id = id;

        setRows((oldRows) => [newRow, ...(oldRows || [])]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus },
        }));

        setSortModel([]);
        setPaginationModel((model) => ({ ...model, page: 0 }));
    };

    const validateRowUpdate = useCallback(
        (row: T) => {
            return new Promise<void>((resolve, reject) => {
                if (!!validateRow) {
                    const errors = validateRow(row);

                    if (errors.length > 0) {
                        reject(errors[0].error);
                    }
                }

                resolve();
            });
        },
        [validateRow]
    );

    const processRowUpdate = async(updatedRow: T & GridRow) => {
        await validateRowUpdate(updatedRow);
        const row = await handleSubmit({
            ...updatedRow,
            id: updatedRow.id === "new" ? null : updatedRow.id,
        });

        setRows((oldRows) => {
            const newRows = [...oldRows];
            let idx = newRows.findIndex((r) => r.id === row.id);
            // If row is not found, check for newly added row.
            idx = idx === -1 ? newRows.findIndex((r) => r.id === "new") : idx;

            if (idx !== -1) {
                newRows[idx] = row;
            }

            return newRows;
        });

        if (!!originalRow.current[editId]) {
            delete originalRow.current[editId];
        }

        if (!!prompt.current?.path) {
            prompt.current.shouldGo = true;
        }

        setEditId(null);
        return row;
    };

    // Updates field outside of datagrids edit mode.
    const updateFields = useCallback(
        (
            id: string,
            values: { field: string; value: any; errors?: any }[]
        ) => {
            if (editId === id) {
                setRows((oldRows) => {
                    const newRows = [...oldRows];
                    const row = newRows.find((r) => r.id === id);

                    if (!!row) {
                        values.forEach(({ field, value, errors }) => {
                            row[field] = value;
                            row[`${field}_errors`] = errors;
                        });
                    }

                    return newRows;
                });
            }
        },
        [setRows, rows, editId]
    );

    const getDetailPanelContent = useCallback(
        (params: GridRowParams) => {
            if (!DetailsComponent) return undefined;

            return (
                <DetailsComponent
                    {...params}
                    updateFields={updateFields}
                    hiddenColumns={hiddenColumns}
                />
            );
        },
        [DetailsComponent, updateFields, hiddenColumns]
    );

    const columnVisibility = useMemo(
        () =>
            Object.fromEntries(
                props.columns.map((c) => [
                    c.field,
                    !hiddenColumns.includes(c.field),
                ])
            ),
        [props.columns, hiddenColumns]
    );

    useEffect(() => {
        if (!highlightedRowId) return;

        // Get sorted & filtered rows as shown in the UI
        const sortedRows = Array.from(
            gridExpandedSortedRowEntriesSelector(apiRef.current.state).values()
        );

        // Find the index of the highlighted row
        const index = sortedRows.findIndex(
            (order) => order.id === highlightedRowId
        );
        if (index === -1) return;

        if (!!props.pagination && !props.hideFooterPagination) {
            // Calculate the correct page for the highlighted row
            const pageSize = paginationModel?.pageSize || 25;
            const page = Math.floor(index / pageSize);

            // Change page if needed
            if (paginationModel.page !== page) {
                setPaginationModel((model) => ({ ...model, page }));
                return;
            }
        }

        // Scroll to the highlighted row
        setTimeout(() => {
            if (apiRef.current?.scrollToIndexes) {
                apiRef.current.scrollToIndexes({ rowIndex: index });
            }
            if (!!setHighlightedRowId) {
                setHighlightedRowId(null);
            }
        }, 2000);
    }, [
        highlightedRowId,
        props.pagination,
        sortModel,
        paginationModel?.page,
        paginationModel?.pageSize,
        props.hideFooterPagination,
    ]);

    // Use prop pagination model if provided (for server-side pagination)
    const effectivePaginationModel = propPaginationModel || paginationModel;

    const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
        if (onPaginationModelChange) {
            onPaginationModelChange(model);
        } else {
            setPaginationModel(model);
            persistGridState(hiddenColumns, model.pageSize);
        }
    }, [onPaginationModelChange, hiddenColumns, persistGridState]);

    if (!gridStateInitialized) return null;

    const columnVisibilityModel = {
        [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
        ...columnVisibility,
    };

    // Set initialState after grid state has been initialized
    const initialState: GridInitialStatePremium = {
        columns: {
            ...persistedGridState?.current?.columns,
            columnVisibilityModel,
        },
        pagination: {
            paginationModel: {
                pageSize:
                    persistedGridState?.current?.pagination?.paginationModel
                        ?.pageSize,
            },
        },
        sorting: {
            sortModel: defaultSortModel,
        },
    };

    return (
        <MuiDataGrid
            columnVisibilityModel={columnVisibilityModel}
            onColumnOrderChange={() => persistGridState(hiddenColumns)}
            paginationMode={paginationMode}
            paginationModel={effectivePaginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            rowCount={paginationMode === "server" ? rowCount : undefined}
            sortingMode={sortingMode}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            initialState={initialState}
            density="compact"
            editMode="row"
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableQuickFilter
            showCellVerticalBorder
            onProcessRowUpdateError={showError}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onCellClick={handleCellClick}
            processRowUpdate={processRowUpdate}
            apiRef={apiRef}
            customToolbar={CustomToolbar}
            customPagination={CustomPagination}
            autosizeOptions={{
                columns: autosizeColumnsByName,
                includeOutliers: true,
                includeHeaders: true,
            }}
            toolbarProps={{
                title: props.title,
                enableColumnSelector: !disableColumnSelector,
                columns: props.columns,
                hiddenColumns,
                setHiddenColumns,
                customToolbarButtons: !editId
                    ? !disableAddRowButton ? [
                        {
                            key: "add",
                            startIcon: <AddIcon />,
                            color: "success",
                            variant: "contained",
                            onClick: handleAdd,
                            children: addRowText || t("common.addNew"),
                            sx: { marginRight: disableColumnSelector ? 1 : 0 },
                        },
                    ] : []
                    : [
                        {
                            key: "cancel",
                            color: "primary",
                            variant: "contained",
                            onClick: () => cancelEdit(editId),
                            children: t("common.cancel"),
                        },
                        {
                            key: "save",
                            color: "success",
                            variant: "contained",
                            onClick: () => stopEditing(),
                            children: t("common.save"),
                            sx: { marginRight: disableColumnSelector ? 1 : 0 },
                        },
                    ],
            }}
            rows={rows || []}
            // Disable edit start via other methods
            onRowEditStart={(_, event: MuiEvent) => {
                event.defaultMuiPrevented = true;
            }}
            // Disable saving when focusing out
            onRowEditStop={(params: GridRowEditStopParams, event: MuiEvent) => {
                if (params.reason === GridRowEditStopReasons.rowFocusOut) {
                    event.defaultMuiPrevented = true;
                } else {
                    revertRows(editId);
                    setEditId(null);
                }
            }}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={() => "auto"}
            {...props}
            sx={{
                maxHeight: "100%",
                height: "initial",
                ...(!editId
                    ? {
                        ".MuiDataGrid-row--editable": {
                            "&:not(.MuiDataGrid-row--editing)": {
                                "& .MuiDataGrid-cell--editable": {
                                    cursor: "pointer",
                                    "&:hover": {
                                        border: `solid 1px ${theme.palette.dark.main}`,
                                    },
                                },
                            },
                        },
                    }
                    : {}),
                "& .MuiDataGrid-cell[data-field='actions']": {
                    justifyContent: "unset",
                },
            }}
        />
    );
}
